import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AiTable as AiTableData, AiTableColumn } from '@apis/aiAgent';
import {
  CheckIcon,
  CopyIcon,
  FloorIcon,
  SearchIconSm,
  CheckIcon as InlineCheckIcon,
  WarningTriangleIcon,
  UsersIcon,
} from '@utils/icons';
import {
  type ChipVariant,
  type SortConfig,
  type SummaryItem,
  SEARCH_THRESHOLD,
  compareValues,
  formatDate,
  getChipVariant,
  isEmptyValue,
} from './aiTable.utils';
import { tradeLabel } from '@utils/tradeLabel';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { groupRows } from './aiTable.grouping';
import { ExportSelectable, type IExportSelectionProps } from './ExportSelectable';
import styles from './AiTable.module.css';

/**
 * Rich rendering for AI-emitted structured tables. Mirrors Trusstor's
 * `AiStructuredTable` — toolbar with title/row-count/status summary +
 * optional search; sticky sortable headers; typed cells (status chips,
 * date, badge_list with overflow, number, progress bar, profession,
 * trend); per-row copy button on hover; no-results state.
 *
 * Trade icons (the upstream `profession` column type) fall back to a
 * plain text label since d-orca doesn't have the Trusstor trade icon
 * set yet. The badge_list overflow tooltip uses a CSS `:hover` reveal
 * panel rather than MUI / Base-UI Tooltip — simpler and dependency-free.
 */

interface IAiTableProps extends IExportSelectionProps {
  table: AiTableData;
}

const MAX_VISIBLE_BADGES = 2;

/**
 * Rows deal in one after another on first paint (`--row-i` drives the
 * CSS delay). Past this index every row lands together, so a hundred-row
 * table finishes in the same half-second as a ten-row one.
 */
export const ROW_STAGGER_CAP = 12;

const rowStaggerStyle = (rowIndex: number): CSSProperties =>
  ({ '--row-i': Math.min(rowIndex, ROW_STAGGER_CAP) }) as CSSProperties;

export function AiTable(props: IAiTableProps) {
  const { table, ...selection } = props;
  const { columns, rows, title } = table;
  const { t, i18n } = useTranslation();
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedRow, setCopiedRow] = useState<number | null>(null);
  // A breakdown's rows are records placed on a dimension: grouped by floor or
  // by item, the same rows answer "what is on each floor" and "where is each
  // activity" — the reader picks (cross-source query spec, D5).
  const groupings = (table.groupings ?? []).filter((id) => columns.some((c) => c.id === id));
  const [groupBy, setGroupBy] = useState<string | null>(
    groupings.length > 1 ? (groupings.find((id) => id === table.groupBy) ?? groupings[0]!) : null,
  );
  const groupColumn = columns.find((c) => c.id === groupBy) ?? null;
  const shownColumns = groupColumn === null ? columns : columns.filter((c) => c !== groupColumn);

  const statusColumn = useMemo(
    () => columns.find((c) => c.type === 'status'),
    [columns],
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(query)),
    );
  }, [rows, searchQuery]);

  const sortedRows = useMemo(() => {
    if (!sortConfig) return filteredRows;
    const sorted = [...filteredRows];
    sorted.sort((a, b) =>
      compareValues(a[sortConfig.columnKey], b[sortConfig.columnKey], sortConfig.direction),
    );
    return sorted;
  }, [filteredRows, sortConfig]);

  const groups = useMemo(
    () => (groupColumn === null ? [{ value: null, rows: sortedRows }] : groupRows(sortedRows, groupColumn.id)),
    [sortedRows, groupColumn],
  );
  const displayRows = useMemo(() => groups.flatMap((group) => group.rows), [groups]);

  // Status summary: aggregate counts per chip variant for the toolbar.
  const summaryItems = useMemo<SummaryItem[]>(() => {
    if (!statusColumn) return [];
    const counts: Record<string, { label: string; count: number; variant: ChipVariant }> = {};
    for (const row of filteredRows) {
      const value = String(row[statusColumn.id] ?? '').trim();
      if (!value) continue;
      const key = value.toLowerCase();
      if (!counts[key]) {
        counts[key] = { label: value, count: 0, variant: getChipVariant(value) };
      }
      counts[key]!.count += 1;
    }
    return Object.values(counts);
  }, [filteredRows, statusColumn]);

  const handleHeaderClick = useCallback((columnKey: string) => {
    setSortConfig((prev) => {
      if (prev?.columnKey === columnKey) {
        if (prev.direction === 'asc') return { columnKey, direction: 'desc' };
        return null;
      }
      return { columnKey, direction: 'asc' };
    });
  }, []);

  const handleCopy = useCallback(
    async (e: React.MouseEvent, rowIndex: number) => {
      e.stopPropagation();
      const row = displayRows[rowIndex];
      if (!row) return;
      const text = columns.map((c) => `${c.label}: ${row[c.id] ?? ''}`).join('\n');
      try {
        await navigator.clipboard.writeText(text);
        setCopiedRow(rowIndex);
        setTimeout(() => setCopiedRow(null), 1500);
      } catch {
        // clipboard unavailable; silently ignore
      }
    },
    [columns, displayRows],
  );

  const getSortIndicator = (columnKey: string): string => {
    if (sortConfig?.columnKey !== columnKey) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const renderCell = (value: unknown, column: AiTableColumn): ReactNode => {
    const strValue = String(value ?? '');
    if (isEmptyValue(value)) return <span className={styles.emptyValue}>—</span>;

    switch (column.type) {
      case 'status': {
        const variant = getChipVariant(strValue);
        return (
          <span className={`${styles.chip} ${styles[variant] ?? ''}`}>{strValue}</span>
        );
      }
      case 'date':
        return <span className={styles.dateCell}>{formatDate(strValue, i18n.language)}</span>;
      case 'badge_list':
        return renderBadgeList(value, column);
      case 'number': {
        const num = parseFloat(strValue);
        if (Number.isNaN(num)) return strValue;
        const formatted = Number.isInteger(num) ? String(num) : num.toFixed(1);
        return <span className={styles.numberCell}>{formatted}</span>;
      }
      case 'progress': {
        const numValue =
          typeof value === 'number' ? value : parseFloat(strValue) || 0;
        const clamped = Math.min(Math.max(numValue, 0), 100);
        const fillClass =
          numValue >= 70
            ? styles.progressFillHigh
            : numValue >= 40
              ? styles.progressFillMid
              : styles.progressFillLow;
        return (
          <div className={styles.progressWrapper}>
            <div className={styles.progressBar}>
              <div className={fillClass} style={{ width: `${clamped}%` }} />
            </div>
            <span className={styles.progressLabel}>{Math.round(numValue)}%</span>
          </div>
        );
      }
      case 'profession':
        // No d-orca trade icon set — render the trade label as plain text.
        // A value may be a display name or a bare trade id (issues carry only
        // the id); `tradeLabel` localizes an id and leaves a name readable.
        return <span className={styles.professionCell}>{tradeLabel(t, strValue)}</span>;
      case 'trend': {
        const trendNum = parseFloat(strValue);
        if (Number.isNaN(trendNum)) return strValue;
        const trendClass =
          trendNum > 0
            ? styles.trendPositive
            : trendNum < 0
              ? styles.trendNegative
              : styles.trendNeutral;
        const arrow = trendNum > 0 ? '▲' : trendNum < 0 ? '▼' : '—';
        return (
          <span className={trendClass}>
            {arrow} {trendNum > 0 ? '+' : ''}
            {trendNum.toFixed(1)}%
          </span>
        );
      }
      default:
        return strValue;
    }
  };

  const renderBadgeList = (value: unknown, column: AiTableColumn): ReactNode => {
    const defaultIcon = (column as AiTableColumn & { icon?: string }).icon;
    type Badge = { icon?: string; text: string };
    let badges: Badge[];
    if (Array.isArray(value)) {
      badges = (value as Array<string | Badge>).map((item) =>
        typeof item === 'string'
          ? { icon: defaultIcon, text: item }
          : { icon: item.icon || defaultIcon, text: item.text },
      );
    } else if (typeof value === 'string' && value.trim()) {
      badges = value
        .split(',')
        .map((s) => ({ icon: defaultIcon, text: s.trim() }))
        .filter((b) => b.text);
    } else {
      badges = [];
    }
    if (badges.length === 0) return <span className={styles.emptyValue}>—</span>;
    const visible = badges.slice(0, MAX_VISIBLE_BADGES);
    const remaining = badges.length - MAX_VISIBLE_BADGES;
    return (
      <span className={styles.badgeList}>
        {visible.map((badge, i) => (
          <span key={i} className={styles.badgeTag}>
            {renderBadgeIcon(badge.icon)}
            {badge.text}
          </span>
        ))}
        {remaining > 0 && (
          <span className={styles.badgeMoreWrapper}>
            <span className={styles.badgeMore}>+{remaining}</span>
            <span className={styles.badgePopover} role="tooltip">
              {badges.map((badge, i) => (
                <span key={i} className={styles.badgeTag}>
                  {renderBadgeIcon(badge.icon)}
                  {badge.text}
                </span>
              ))}
            </span>
          </span>
        )}
      </span>
    );
  };

  const getAlignClass = (column: AiTableColumn): string => {
    const align =
      (column as AiTableColumn & { align?: string }).align ??
      (column.type === 'number' || column.type === 'trend'
        ? 'right'
        : column.type === 'progress'
          ? 'center'
          : undefined);
    if (align === 'center') return styles.alignCenter ?? '';
    if (align === 'right') return styles.alignRight ?? '';
    return '';
  };

  if (columns.length === 0) return null;
  const showSearch = rows.length >= SEARCH_THRESHOLD;

  return (
    <ExportSelectable {...selection}>
      <div className={styles.tableContainer}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            {title && (
              <>
                <span className={styles.title}>{title}</span>
                <span className={styles.titleDivider} />
              </>
            )}
            <span className={styles.summaryCount}>
              {filteredRows.length}
              {filteredRows.length !== rows.length && ` / ${rows.length}`} {t('aiAgent.table.rowCountSuffix')}
            </span>
            {summaryItems.length > 0 && (
              <>
                <span className={styles.summaryDivider} />
                {summaryItems.map((item) => (
                  <span key={item.label} className={styles.summaryItem}>
                    <span
                      className={`${styles.summaryDot} ${styles[item.variant] ?? ''}`}
                    />
                    {item.count} {item.label}
                  </span>
                ))}
              </>
            )}
          </div>
          {groupColumn !== null && (
            <SegmentedControl
              variant="joined"
              ariaLabel={t('aiAgent.table.groupBy')}
              value={groupColumn.id}
              onChange={setGroupBy}
              options={groupings.map((id) => ({ id, label: columns.find((c) => c.id === id)!.label }))}
            />
          )}
          {showSearch && (
            <div className={styles.searchWrapper}>
              <SearchIconSm size={14} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder={t('aiAgent.table.filterPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          )}
        </div>

        <ScrollArea orientation="both" className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                {shownColumns.map((col) => (
                  <th
                    key={col.id}
                    className={`${styles.headerCell} ${
                      sortConfig?.columnKey === col.id ? styles.headerCellActive : ''
                    }`}
                    onClick={() => handleHeaderClick(col.id)}
                  >
                    <span className={styles.headerContent}>
                      {col.label}
                      {getSortIndicator(col.id) && (
                        <span className={styles.sortIcon}>{getSortIndicator(col.id)}</span>
                      )}
                    </span>
                  </th>
                ))}
                <th className={styles.actionHeader} />
              </tr>
            </thead>
            <tbody>
              {groups.map((group, groupIndex) => {
                const offset = groups.slice(0, groupIndex).reduce((sum, g) => sum + g.rows.length, 0);
                return [
                  groupColumn !== null && (
                    <tr key={`group-${groupIndex}`} className={styles.groupRow}>
                      <td colSpan={shownColumns.length + 1}>
                        {renderCell(group.value, groupColumn)}
                        <span className={styles.groupCount}>{group.rows.length}</span>
                      </td>
                    </tr>
                  ),
                  ...group.rows.map((row, i) => {
                    const rowIndex = offset + i;
                    return (
                      <tr
                        key={rowIndex}
                        className={rowIndex % 2 === 1 ? styles.rowEven : ''}
                        style={rowStaggerStyle(rowIndex)}
                      >
                        {shownColumns.map((col) => (
                          <td key={col.id} className={`${styles.cell} ${getAlignClass(col)}`}>
                            {renderCell(row[col.id], col)}
                          </td>
                        ))}
                        <td className={styles.actionCell}>
                          <button
                            className={styles.copyButton}
                            onClick={(e) => handleCopy(e, rowIndex)}
                            title={t('aiAgent.table.copyRow')}
                            aria-label={t('aiAgent.table.copyRow')}
                            type="button"
                          >
                            {copiedRow === rowIndex ? (
                              <span className={styles.copyCheck}>
                                <CheckIcon size={14} />
                              </span>
                            ) : (
                              <CopyIcon size={14} />
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  }),
                ];
              })}
            </tbody>
          </table>
        </ScrollArea>

        {filteredRows.length === 0 && searchQuery && (
          <div className={styles.noResults}>{t('aiAgent.table.noMatchingRows')}</div>
        )}
      </div>
    </ExportSelectable>
  );
}

/**
 * Maps the icon name keys a `badge_list` column carries onto the d-orca
 * icon set. Unknown names render as no icon.
 */
function renderBadgeIcon(iconName?: string): ReactNode {
  if (!iconName) return null;
  if (iconName === 'floor') return <FloorIcon size={12} />;
  if (iconName === 'worker') return <UsersIcon size={12} />;
  if (iconName === 'warning') return <WarningTriangleIcon size={12} />;
  if (iconName === 'check') return <InlineCheckIcon size={12} />;
  return null;
}
